import React, { useEffect, useState, useRef, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  PatternlibButton,
  PatternlibOverlayContainer,
  PatternlibIcon,
  PatternlibSpinner,
  PatternlibTextlink,
} from "@liebherr/patternlib-react";
import PropTypes from "prop-types";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import FormTextinput from "../form-textinput/FormTextinput";
import Separator from "../separator/Separator";
import styles from "./SendButtonDemo.module.css";

const SendButtonDemo = ({
  part,
  data,
  onError,
  productInformation,
  showModal,
}) => {
  const { t } = useTranslation();

  const [isSubmit, setIsSubmit] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [values, setValues] = useState(null);
  const [show, setShow] = useState(showModal);
  const overlayRef = useRef();

  // the useRef Hook allows to persist data between renders
  const partRef = useRef();
  const dataRef = useRef();

  // useNavigate Hook to navigate to different pages
  const navigate = useNavigate();

  // function to change active state of sendButton and displaying a spinner when sending mails
  const reducer = (state, action) => {
    switch (action.type) {
      case "toggleMailIsPending":
        return {
          disableConfirmButton: !state.disableConfirmButton,
          showSpinner: !state.showSpinner,
        };
      default:
        return state;
    }
  };

  // useReducer to toggle the sendButton disabled and a spinner displayed (while sending mails)
  const [state, dispatch] = useReducer(reducer, {
    disableConfirmButton: false,
    showSpinner: false,
  });

  const GetValuesFromFormik = () => {
    // grab values and submitForm from context
    const { values, submitForm, handleReset } = useFormikContext();

    // listen to changes to isSubmit state
    useEffect(() => {
      if (isSubmit) {
        submitForm();
      }
    }, [isSubmit]);

    // listen to changes to isReset state
    useEffect(() => {
      if (isReset) {
        handleReset();
      }
    }, [isReset]);

    return null;
  };

  const ValidationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("SendButton.validEmail"))
      .required(t("SendButton.required")),
  });

  const handleRequestError = (err) => {
    onError(err);
  };

  // function to be calles when user opens modal
  const onButtonClick = () => {
    setShow(true);
    setIsReset(false);

    // reuest consent status for save contact data
    const event = new CustomEvent("hyzRequestConsentStatus", {
      bubbles: true,
    });
    window.dispatchEvent(event);
  };

  // function to be calles when user confirms modal
  const onConfirm = () => {
    if (!state.disableConfirmButton) {
      setIsSubmit(true);
    }
  };

  // function to be called when user cancels or closes modal
  const onClose = () => {
    setIsReset(true);
    setShow(false);
  };

  const sendMail = (data) => {
    dispatch({ type: "toggleMailIsPending" });

    // gather request options
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-type": "application/json",
        "x-api-key": window.env.REACT_APP_BACKEND_API_KEY,
      },
      body: JSON.stringify(data),
    };

    const apiUrl =
      process.env.NODE_ENV === "production" ? "/" : "http://localhost:9001/";
    // make the fetch request to the backend
    fetch(`${apiUrl}api/v1/demo`, requestOptions)
      .then(async (response) => {
        if (response.ok && response.status === 200) {
          dispatch({ type: "toggleMailIsPending" });
          setShow(false);
          navigate("/confirm");
        } else if (!response.ok && response.status === 401) {
          throw new Error("Authorisierung fehlgeschlagen");
        } else {
          throw new Error(response.status, response.statusText);
        }
      })
      .catch((err) => {
        dispatch({ type: "toggleMailIsPending" });
        setShow(false);
        handleRequestError(err);
      });
  };

  useEffect(() => {
    if (show) {
      const ref = overlayRef.current;
      const element = ref.shadowRoot.querySelector(".backdrop");
      const content = ref.shadowRoot.querySelector(".content");

      if (content) {
        content.style.zIndex = "9999";
      } else {
        setShow(false);
        setTimeout(() => {
          setShow(true);
        }, 1000);
      }

      if (element !== null) {
        element.addEventListener("click", onClose, false);

        return () => {
          element.removeEventListener("click", onClose, true);
        };
      }
    }
  }, [show]);

  useEffect(() => {
    if (part !== undefined) {
      partRef.current = part;
    }
  }, [part]);

  useEffect(() => {
    if (data !== undefined) {
      dataRef.current = data;
    }
  }, [data]);

  useEffect(() => {
    // get values from formik after submit
    if (values !== null) {
      const data = {
        customer: {
          email: values.email,
        },
        product: {
          key: partRef.current,
        },
      };
      // console.log("the data to be send via Email:", data);

      sendMail(data);
    }
  }, [values]);

  return (
    <>
      <PatternlibOverlayContainer
        target="center"
        show={show}
        disable-click-handlers
        ref={overlayRef}
      >
        <PatternlibButton
          onClick={onButtonClick}
          label={t("SendButton.requestOfferDemo")}
          size="small"
          slot="element"
          display="block"
          width="100%"
        ></PatternlibButton>
        <div slot="content" className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <div>
              <h2>{t("SendButton.offerModalTitleDemo")}</h2>
              {productInformation && (
                <>
                  <Separator space="s"></Separator>
                  <h4 className={styles.key}>
                    {t("infotexts.general.key.title") +
                      ": " +
                      productInformation["P"].current}
                  </h4>
                  <Separator space="xxs"></Separator>
                </>
              )}
            </div>
            <div className={styles.closeIcon}>
              <PatternlibIcon icon-name="x" onClick={onClose}></PatternlibIcon>
            </div>
          </div>
          <div className={styles.modalContainer}>
            <div className={styles.modalWrapper}>
              <Formik
                initialValues={{
                  email: "",
                }}
                validationSchema={ValidationSchema}
                validate={() => {
                  setIsSubmit(false);
                }}
                onSubmit={(values, { resetForm }) => {
                  setIsSubmit(false);
                  setValues(values);
                  resetForm();

                  // if checked, set consent
                  if (values.consent === "checked") {
                    // reuest consent to get set
                    const event = new CustomEvent("hyzRequestSetConsent", {
                      bubbles: true,
                    });
                    window.dispatchEvent(event);
                  }
                }}
              >
                {({ errors, touched, handleChange, values }) => (
                  <Form>
                    <FormTextinput
                      label={t("SendButton.emailLabel")}
                      placeholder={t("SendButton.emailPlaceholder")}
                      initialValue={values.email}
                      onChange={handleChange("email")}
                      error={
                        errors.email && touched.email ? errors.email : null
                      }
                      type="email"
                    ></FormTextinput>
                    {/* adding spinner with two separators, removing the first if the spinner is shown (to make sure the spinner is seen in full heigth) */}
                    {!state.showSpinner && <Separator space="m"></Separator>}
                    {state.showSpinner && (
                      <PatternlibSpinner spin></PatternlibSpinner>
                    )}
                    <GetValuesFromFormik />
                  </Form>
                )}
              </Formik>
            </div>
          </div>
          <div className={styles.modalFooter}>
            <PatternlibTextlink
              label={t("SendButton.offerModalCancel")}
              size="small"
              onClick={onClose}
            ></PatternlibTextlink>
            <PatternlibButton
              label-text-align="left"
              icon-position="right"
              label={t("SendButton.offerModalConfirm")}
              size="small"
              disabled={state.disableConfirmButton}
              onClick={onConfirm}
            >
              <PatternlibIcon
                icon-name="font-arrow"
                slot="icon"
              ></PatternlibIcon>
            </PatternlibButton>
          </div>
        </div>
      </PatternlibOverlayContainer>
    </>
  );
};

SendButtonDemo.propTypes = {
  part: PropTypes.string,
  data: PropTypes.any,
  onError: PropTypes.func.isRequired,
  showModal: PropTypes.bool,
};

SendButtonDemo.defaultProps = {
  part: "",
  data: null,
  showModal: false,
};

export default SendButtonDemo;
